import { useGlobalState } from "../contexts/GlobalStateContext";
import { useState } from "react";
import Create from "./Create";

const PostOptions = ({ post, closeOptions }) => {
  const {
    globalState,
    setUser,
    removePostWithFade,
    setMessageJD,
    incrementRefreshKey,
    setSessionExpired,
    setJustPosted,
    setDuplicatePost,
    setFullScreenPost,
  } = useGlobalState();
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const [showOptions, setShowOptions] = useState(true);
  const [showEditDraft, setShowEditDraft] = useState(null);
  const actionMessages = {
    delete: "Post Successfully Deleted.",
    save: "Post Successfully Added To Saved.",
    feature: "Post Successfully added to Featured.",
    "make private": "Post Successfully Added To Private.",
    unsave: "Post Successfully Removed From Saved",
    unfeature: "Post Successfully Removed From Featured.",
    "make public": "Post Successfully Made Public.",
    "delete draft": "Draft Successfully Deleted.",
    publish: "Post Successfully Published. Check it out!",
  };


  const handleShowEdit = (uuid) => {
    setShowOptions(false);
    setShowEditDraft(uuid);
  };

  const handleAction = (action, message, endpoint) => {
    const customDialog = document.createElement("div");
    customDialog.id = "customConfirmDialog";
    customDialog.style.display = "block";

    const confirmMessage = document.createElement("p");
    confirmMessage.id = "confirmMessage";
    confirmMessage.textContent = message;

    const confirmBtn = document.createElement("button");
    confirmBtn.id = "confirmBtn";

    // Detect if this is an info-only message (used after report)
    const isOkayOnly = action === "report" && (message.startsWith("This post has already been reported") || message.startsWith("Your report has been received"));

    confirmBtn.textContent = isOkayOnly ? "Okay" : "Yes";

    customDialog.appendChild(confirmMessage);
    customDialog.appendChild(confirmBtn);

    // If this is a Yes/No modal, add cancel button
    let cancelBtn = null;
    if (!isOkayOnly) {
      cancelBtn = document.createElement("button");
      cancelBtn.id = "cancelBtn";
      cancelBtn.textContent = "No";
      customDialog.appendChild(cancelBtn);
    }

    document.body.appendChild(customDialog);


    return new Promise((resolve, reject) => {
      confirmBtn.onclick = () => {
        customDialog.style.display = "none";

        // If it's just an okay message (after report), don't fetch
        if (isOkayOnly) return;

        fetch(`${baseUrl}/${endpoint}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ uuid: post.uuid }),
        })
          .then(async (response) => {
            const data = await response.json();

            if (!response.ok) {
              if (response.status === 401) {
                setSessionExpired(true);
                return;
              }

              throw new Error(data.message || "Unknown error occurred");
            }

            if (data.status === "duplicate") {
              setDuplicatePost(data.post);
              return;
            }

            if (data.status === "success") {
              closeOptions();

              if (action === "report") {
                const reportMsg = data.alreadyReported
                  ? "This post has already been reported by you."
                  : "Your report has been received. We will review this post shortly.";

                // Show result modal with just "Okay"
                handleAction("report", reportMsg);
                return;
              }
              if (action === "delete draft") {
                removePostWithFade(post.uuid);
                setFullScreenPost(null);

                setTimeout(() => {
                  incrementRefreshKey();
                }, 550); // must be > fade duration
              }
              else if (action === "delete") {
                setUser((prevUser) => ({
                  ...prevUser,
                  noOfPosts: Math.max((prevUser.noOfPosts || 1) - 1, 0),
                }));

                removePostWithFade(post.uuid);
                incrementRefreshKey();
                setFullScreenPost(null);
              } else if (action === "make private") {
                setUser((prevUser) => ({
                  ...prevUser,
                  noOfPosts: Math.max((prevUser.noOfPosts || 1) - 1, 0),
                }));
                removePostWithFade(post.uuid);
                incrementRefreshKey();
              } else if (action === "unsave" || action === "unfeature") {
                removePostWithFade(post.uuid);
                incrementRefreshKey();
                setFullScreenPost(null);
              } else if (action === "publish" || action === "make public") {
                setUser((prevUser) => ({
                  ...prevUser,
                  noOfPosts: Math.max((prevUser.noOfPosts || 1) + 1, 0),
                }));

                removePostWithFade(post.uuid);
                incrementRefreshKey();
              }

              if (actionMessages[action]) {
                setMessageJD(actionMessages[action]);
              }

              if (action === "publish") {
                delete post.draft;
                setJustPosted(post);

                // ✅ fade out the draft
                removePostWithFade(post.uuid);
                incrementRefreshKey();
              }



              resolve();
            } else {
              alert(`Failed to ${action}: ${data.message}`);
              reject(new Error(`Failed to ${action}: ${data.message}`));
            }
          })
          .catch((error) => {
            alert(`Error: ${error.message}`);
            reject(error);
          });
      };

      // Cancel button handler
      if (!isOkayOnly && cancelBtn) {
        cancelBtn.onclick = () => {
          customDialog.style.display = "none";
        };
      }
    });
  };


  const renderButtons = () => {
    let buttons = [];

    if (
      post.username === globalState.user.username &&
      !globalState.viewAsPublic
    ) {
      if (post.private) {
        buttons.push({
          text: "Make Public",
          className: "btn-primary",
          action: () =>
            handleAction(
              "make public",
              "Make this post public?",
              "php/make_public.php"
            ),
        });
        buttons.push({
          text: "Delete",
          className: "btn-danger",
          action: () =>
            handleAction(
              "delete",
              "Delete this private post?",
              "php/delete_private_post.php"
            ),
        });
      } else if (post.draft) {
        buttons.push({
          text: "Edit",
          className: "btn-feature",
          action: () => handleShowEdit(post.uuid),
        });
        buttons.push({
          text: "Publish",
          className: "btn-primary",
          action: () =>
            handleAction(
              "publish",
              "Publish this draft?",
              "php/publish_draft.php"
            ),
        });
        buttons.push({
          text: "Delete",
          className: "btn-danger",
          action: () =>
            handleAction(
              "delete draft",
              "Delete this draft?",
              "php/delete_draft.php"
            ),
        });
      } else {
        buttons.push({
          text: post.saved ? "Unsave" : "Save",
          className: "btn-feature",
          action: () =>
            handleAction(
              post.saved ? "unsave" : "save",
              `${post.saved ? "Unsave" : "Save"} this post?`,
              `php/${post.saved ? "unsave_post" : "save_post"}.php`
            ),
        });
        buttons.push({
          text: post.featured ? "Unfeature" : "Feature",
          className: "btn-primary",
          action: () =>
            handleAction(
              post.featured ? "unfeature" : "feature",
              `${post.featured ? "Unfeature" : "Feature"} this post?`,
              `php/${post.featured ? "unfeature_post" : "feature_post"}.php`
            ),
        });
        buttons.push({
          text: "Make Private",
          className: "btn-default",
          action: () =>
            handleAction(
              "make private",
              "Make this post private?",
              "php/make_private.php"
            ),
        });
        buttons.push({
          text: "Delete",
          className: "btn-danger",
          action: () =>
            handleAction("delete", "Delete this post?", "php/delete_post.php"),
        });
      }
    } else {
      buttons.push({
        text: post.saved ? "Unsave" : "Save",
        className: "btn-default",
        action: () =>
          handleAction(
            post.saved ? "unsave" : "save",
            `${post.saved ? "Unsave" : "Save"} this post?`,
            `php/${post.saved ? "unsave_post" : "save_post"}.php`
          ),
      });
      buttons.push({
        text: post.featured ? "Unfeature" : "Feature",
        className: "btn-primary",
        action: () =>
          handleAction(
            post.featured ? "unfeature" : "feature",
            `${post.featured ? "Unfeature" : "Feature"} this post?`,
            `php/${post.featured ? "unfeature_post" : "feature_post"}.php`
          ),
      });
      buttons.push({
        text: "Report",
        className: "btn-danger",
        action: () =>
          handleAction("report", "Report this post?", "php/report_post.php"),
      });
    }

    return buttons.map((button, index) => (
      <button
        key={index}
        className={`postOptionsButton ${button.className}`}
        onClick={button.action}
      >
        {button.text}
      </button>
    ));
  };

  return (
    <>
      {showEditDraft && (
        <Create
          uuid={showEditDraft}
          onAction={() => closeOptions()}
          onClose={() => { setShowEditDraft(null); closeOptions() }}
        />
      )}
      {showOptions && (
        <div id="showPostOptions" className="post-options-modal">
          <div className="post-options-content">
            <span className="closePostOptions" onClick={() => closeOptions()}>
              &times;
            </span>
            <div className="post-options-content">{renderButtons()}</div>
          </div>
        </div>
      )}
    </>
  );
};

export default PostOptions;
